import React, { useEffect, useMemo, useRef, useState } from "react";
import styles from "./videoBriefing.module.css";
// import progressRgGif from "../../../assets/images/progress_rg.gif";
import InlineSpinner from "../../Common/InlineSpinner/InlineSpinner";

import viewIcon from "../../../assets/images/show.svg";
import downloadIcon from "../../../assets/images/download.svg";
import DocumentPreviewModal from "../preview/DocumentPreviewModal";
import {
  getPreviewUnavailableMessage,
  isPresentationFile,
  resolvePreviewTarget,
} from "../preview/previewTargets";
import useFilesApi from "../../../api/filesApi";
import { useUserCapabilities } from "../../../auth.capabilities";

const VIDEO_EXTENSIONS = new Set(["mp4", "webm", "ogg"]);

function downloadBlob(blob, name) {
  if (!blob) return;
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = name || "download";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.setTimeout(() => {
    window.URL.revokeObjectURL(url);
  }, 0);
}

const inferContentType = (fileName) => {
  const ext = String(fileName || "")
    .split(".")
    .pop()
    .toLowerCase();
  if (ext === "pdf") return "application/pdf";
  if (ext === "mp4") return "video/mp4";
  if (ext === "webm") return "video/webm";
  if (ext === "ogg") return "video/ogg";
  if (ext === "docx") {
    return "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
  }
  if (ext === "doc") return "application/msword";
  if (ext === "xlsx") {
    return "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
  }
  if (ext === "xls") return "application/vnd.ms-excel";
  if (ext === "pptx") {
    return "application/vnd.openxmlformats-officedocument.presentationml.presentation";
  }
  if (ext === "ppt") return "application/vnd.ms-powerpoint";
  if (ext === "txt") return "text/plain";
  if (ext === "csv") return "text/csv";
  if (ext === "html" || ext === "htm") return "text/html";
  return "";
};

const ensureBlobType = (blob, contentType, fileName) => {
  if (!blob) return null;
  const inferred = contentType || inferContentType(fileName);
  if (!inferred) return { blob, contentType: contentType || "" };
  if (blob.type === inferred) return { blob, contentType: inferred };
  return { blob: new Blob([blob], { type: inferred }), contentType: inferred };
};

const pickPreviewFileName = (...values) => {
  const names = values.filter(
    (value) => typeof value === "string" && value.trim().length > 0,
  );
  const withExtension = names.find((name) =>
    /\.[a-z0-9]{2,5}$/i.test(name.trim()),
  );
  return withExtension || names[0] || "download";
};

const normalizeFile = (file) => {
  const name =
    file?.name || file?.fileName || file?.title || file?.documentName || "File";

  const url =
    file?.url || file?.fileUrl || file?.link || file?.downloadUrl || "";

  const fileId =
    file?.id ||
    file?.fileId ||
    file?.documentId ||
    file?.referenceId ||
    file?.attachmentId ||
    file?.file?.id;

  const restricted =
    Boolean(file?.restricted) ||
    Boolean(file?.isRestricted) ||
    Boolean(file?.private);

  return { name, url, restricted, fileId, raw: file };
};

const getFileKey = (file) =>
  String(file?.raw?.id || file?.fileId || file?.name || "briefing-file");

const getFileExtensionLabel = (name) => {
  const ext = String(name || "").split(".").pop()?.trim().toUpperCase();
  if (!ext || ext === String(name || "").trim().toUpperCase()) return "FILE";
  return ext.slice(0, 5);
};

const isVideoFile = (file) => {
  const extension = String(file?.name || "")
    .split(".")
    .pop()
    .toLowerCase();
  const contentType = String(
    file?.raw?.contentType || file?.raw?.mimeType || inferContentType(file?.name),
  ).toLowerCase();

  return contentType.startsWith("video/") || VIDEO_EXTENSIONS.has(extension);
};

const getApiErrorMessage = (error, fallback) =>
  error?.response?.data?.message ||
  error?.response?.data?.error ||
  error?.message ||
  fallback;

const getFileActionErrorMessage = (error, action, file) => {
  const status = Number(error?.response?.status ?? error?.status ?? 0);
  const title = String(error?.response?.data?.title ?? "").trim().toLowerCase();

  if (status === 404 || title === "not found") {
    if (action === "preview" && isPresentationFile(file)) {
      return getPreviewUnavailableMessage(file);
    }
    return "This file could not be found. It may have been removed or is no longer available.";
  }

  if (status === 403) {
    return `You do not have permission to ${action} this briefing material.`;
  }

  return getApiErrorMessage(
    error,
    action === "preview"
      ? getPreviewUnavailableMessage(file)
      : "Could not download this briefing material.",
  );
};

const RequestAccessButton = ({ label, onClick }) => (
  <button
    type="button"
    className={styles.restrictBtn}
    onClick={onClick}
    aria-label={label}
  >
    <span>{label}</span>
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <path
        fill="none"
        stroke="#fff"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.5"
        d="M14.76 12H6.832m0 0c0-.275-.057-.55-.17-.808L4.285 5.814c-.76-1.72 1.058-3.442 2.734-2.591L20.8 10.217c1.46.74 1.46 2.826 0 3.566L7.02 20.777c-1.677.851-3.495-.872-2.735-2.591l2.375-5.378A2 2 0 0 0 6.83 12"
      />
    </svg>
  </button>
);

const VideoBriefing = ({
  briefingMaterials = [],
  filesLoading = false,
  filesError = "",
  onRequestAccess,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState(0);
  const [type, setType] = useState("");
  const [actionError, setActionError] = useState("");
  const [inlineVideoFiles, setInlineVideoFiles] = useState({});
  const [loadingInlineVideos, setLoadingInlineVideos] = useState({});
  const [inlineVideoErrors, setInlineVideoErrors] = useState({});
  const { downloadFileBlob } = useFilesApi();
  const caps = useUserCapabilities() || {};
  const hasAdminRights = caps.hasAdminRights === true;
  const inlineVideoFilesRef = useRef({});
  const loadingInlineVideosRef = useRef({});
  const inlineVideoErrorsRef = useRef({});

  const normalizedBriefingMaterials = useMemo(
    () =>
      Array.isArray(briefingMaterials)
        ? briefingMaterials.map(normalizeFile)
        : [],
    [briefingMaterials],
  );
  const accessibleBriefingMaterials = normalizedBriefingMaterials.filter(
    (item) => !item.restricted,
  );
  const briefingAllRestricted =
    !hasAdminRights &&
    normalizedBriefingMaterials.length > 0 &&
    accessibleBriefingMaterials.length === 0;
  const showCard =
    Boolean(filesError) ||
    (!filesLoading && normalizedBriefingMaterials.length > 0);

  useEffect(() => {
    inlineVideoFilesRef.current = inlineVideoFiles;
  }, [inlineVideoFiles]);

  useEffect(() => {
    loadingInlineVideosRef.current = loadingInlineVideos;
  }, [loadingInlineVideos]);

  useEffect(() => {
    inlineVideoErrorsRef.current = inlineVideoErrors;
  }, [inlineVideoErrors]);

  useEffect(() => {
    return () => {
      Object.values(inlineVideoFilesRef.current).forEach((file) => {
        if (file?.blobUrl) {
          window.URL.revokeObjectURL(file.blobUrl);
        }
      });
    };
  }, []);

  const handleRequestAccess = () => {
    if (typeof onRequestAccess === "function") {
      onRequestAccess({ kind: "briefingMaterials" });
      return;
    }
    window.alert("Request access flow not wired yet.");
  };

  useEffect(() => {
    const activeFileKeys = new Set(normalizedBriefingMaterials.map(getFileKey));

    setInlineVideoFiles((prev) => {
      let changed = false;
      const next = {};

      Object.entries(prev).forEach(([fileKey, file]) => {
        if (activeFileKeys.has(fileKey)) {
          next[fileKey] = file;
          return;
        }

        if (file?.blobUrl) {
          window.URL.revokeObjectURL(file.blobUrl);
        }
        changed = true;
      });

      return changed ? next : prev;
    });

    setLoadingInlineVideos((prev) => {
      const next = Object.fromEntries(
        Object.entries(prev).filter(([fileKey]) => activeFileKeys.has(fileKey)),
      );
      return Object.keys(next).length === Object.keys(prev).length ? prev : next;
    });

    setInlineVideoErrors((prev) => {
      const next = Object.fromEntries(
        Object.entries(prev).filter(([fileKey]) => activeFileKeys.has(fileKey)),
      );
      return Object.keys(next).length === Object.keys(prev).length ? prev : next;
    });
  }, [normalizedBriefingMaterials]);

  const loadPreviewFile = async (file, onProgress) => {
    if (file.restricted && !hasAdminRights) return null;

    const { method, target } = resolvePreviewTarget(file);
    if (!target) return null;

    if (method === "url" && target.url) {
      return {
        name: target.name || file.name,
        url: target.url,
        blobUrl: "",
        contentType: target.contentType || "",
      };
    }

    if (!target.fileId) return null;

    const res = await downloadFileBlob(target.fileId, onProgress);
    if (!res?.blob) return null;

    const fileName = pickPreviewFileName(
      res.name && res.name !== "download" ? res.name : "",
      target.raw?.name,
      target.raw?.fileName,
      target.raw?.documentName,
      target.name,
      file.raw?.name,
      file.raw?.fileName,
      file.raw?.documentName,
      file.name,
    );
    const adjusted = ensureBlobType(res.blob, res.contentType, fileName);
    if (!adjusted?.blob) return null;

    return {
      name: fileName,
      url: file.url || "",
      blobUrl: window.URL.createObjectURL(adjusted.blob),
      contentType:
        adjusted.contentType ||
        res.contentType ||
        target.contentType ||
        target.raw?.contentType ||
        target.raw?.mimeType ||
        file.raw?.contentType ||
        file.raw?.mimeType ||
        "",
    };
  };

  const handlePreview = async (file) => {
    try {
      setActionError("");
      setDownloading(true);
      setDownloadProgress(0);
      setType("Loading file");

      const nextPreviewFile = await loadPreviewFile(file, (percent) => {
        setDownloadProgress(percent);
      });
      if (!nextPreviewFile) {
        setActionError(getPreviewUnavailableMessage(file));
        return;
      }

      setPreviewFile(nextPreviewFile);
      setIsModalOpen(true);
    } catch (error) {
      setPreviewFile(null);
      setIsModalOpen(false);
      setActionError(getFileActionErrorMessage(error, "preview", file));
    } finally {
      setDownloading(false);
      setDownloadProgress(0);
    }
  };

  useEffect(() => {
    let isActive = true;

    normalizedBriefingMaterials.forEach((file) => {
      if (!isVideoFile(file)) return;
      if (!file.fileId) return;
      if (file.restricted && !hasAdminRights) return;

      const fileKey = getFileKey(file);
      if (inlineVideoFilesRef.current[fileKey]?.blobUrl) return;
      if (loadingInlineVideosRef.current[fileKey]) return;

      setLoadingInlineVideos((prev) =>
        prev[fileKey] ? prev : { ...prev, [fileKey]: true },
      );

      loadPreviewFile(file)
        .then((nextPreviewFile) => {
          if (!nextPreviewFile) return;
          if (!isActive) {
            if (nextPreviewFile.blobUrl) {
              window.URL.revokeObjectURL(nextPreviewFile.blobUrl);
            }
            return;
          }

          setInlineVideoFiles((prev) => {
            const previousFile = prev[fileKey];
            if (
              previousFile?.blobUrl &&
              previousFile.blobUrl !== nextPreviewFile.blobUrl
            ) {
              window.URL.revokeObjectURL(previousFile.blobUrl);
            }

            return {
              ...prev,
              [fileKey]: nextPreviewFile,
            };
          });

          setInlineVideoErrors((prev) => {
            if (!(fileKey in prev)) return prev;
            const next = { ...prev };
            delete next[fileKey];
            return next;
          });
        })
        .catch(() => {
          if (!isActive) return;
          setInlineVideoErrors((prev) => ({
            ...prev,
            [fileKey]: "Unable to load this briefing video inline.",
          }));
        })
        .finally(() => {
          if (!isActive) return;
          setLoadingInlineVideos((prev) => {
            if (!(fileKey in prev)) return prev;
            const next = { ...prev };
            delete next[fileKey];
            return next;
          });
        });
    });

    return () => {
      isActive = false;
    };
  }, [
    normalizedBriefingMaterials,
    hasAdminRights,
    downloadFileBlob,
  ]);

  const handleDownload = async (file) => {
    try {
      setActionError("");
      setDownloading(true);
      setDownloadProgress(0);
      setType("Downloading");

      if (!file.fileId) return;
      if (file.restricted && !hasAdminRights) return;

      const res = await downloadFileBlob(file.fileId, (percent) => {
        setDownloadProgress(percent);
      });
      if (!res?.blob) return;

      const fileName = pickPreviewFileName(
        res.name && res.name !== "download" ? res.name : "",
        file.raw?.name,
        file.raw?.fileName,
        file.raw?.documentName,
        file.name,
      );
      downloadBlob(res.blob, fileName);
    } catch (error) {
      setActionError(getFileActionErrorMessage(error, "download", file));
    } finally {
      setDownloading(false);
      setDownloadProgress(0);
    }
  };

  if (!showCard) return null;

  return (
    <>
      <div className={styles.videoCard}>
        <h4>Briefing Material</h4>

        {filesError ? <p className={styles.statusText}>{filesError}</p> : null}
        {actionError ? (
          <p className={styles.statusError} role="alert">
            {actionError}
          </p>
        ) : null}

        {briefingAllRestricted ? (
          <RequestAccessButton
            label="Request Access to This Briefing Material"
            onClick={handleRequestAccess}
          />
        ) : null}

        {!briefingAllRestricted && normalizedBriefingMaterials.length > 0 ? (
          <div className={styles.itemsList}>
            {normalizedBriefingMaterials.map((file) => {
              const videoFile = isVideoFile(file);
              const disabled = !file.fileId || (file.restricted && !hasAdminRights);
              const fileKey = getFileKey(file);
              const inlineVideo = inlineVideoFiles[fileKey];
              const inlineVideoLoading = loadingInlineVideos[fileKey] === true;
              const inlineVideoError = inlineVideoErrors[fileKey];

              return (
                <article
                  key={fileKey}
                  className={styles.itemCard}
                >
                  {videoFile ? (
                    <>
                      {inlineVideo?.blobUrl ? (
                        <div className={styles.videoWrapper}>
                          <video
                            className={styles.inlineVideo}
                            controls
                            preload="metadata"
                            aria-label={`${file.name} briefing video`}
                          >
                            <source
                              src={inlineVideo.blobUrl}
                              type={
                                inlineVideo.contentType ||
                                file.raw?.contentType ||
                                file.raw?.mimeType ||
                                "video/mp4"
                              }
                            />
                            Your browser does not support the video tag.
                          </video>
                        </div>
                      ) : inlineVideoLoading ? (
                        <div className={styles.videoWrapper}>
                          <div className={styles.inlineVideoLoading}>
                            <InlineSpinner />
                            <p>Loading briefing video...</p>
                          </div>
                        </div>
                      ) : inlineVideoError ? (
                        <div className={styles.videoWrapper}>
                          <div className={styles.inlineVideoFallback}>
                            <p>{inlineVideoError}</p>
                          </div>
                        </div>
                      ) : (
                        <div className={styles.videoWrapper}>
                          <div className={styles.inlineVideoLoading}>
                            <InlineSpinner />
                            <p>Preparing briefing video...</p>
                          </div>
                        </div>
                      )}
                      <div className={styles.videoInfo}>
                        <p className={styles.tileTitle}>{file.name}</p>
                        <p className={styles.tileMeta}>
                          {disabled
                            ? "Briefing video"
                            : "Play inline here or use the fullscreen control in the player."}
                        </p>
                      </div>
                    </>
                  ) : (
                    <div
                      className={styles.previewTile}
                    >
                      <div className={styles.tileBadge}>
                        {getFileExtensionLabel(file.name)}
                      </div>
                      <div className={styles.tileContent}>
                        <p className={styles.tileTitle}>{file.name}</p>
                        <p className={styles.tileMeta}>Briefing file</p>
                      </div>
                    </div>
                  )}

                  <div className={styles.fileActions}>
                    <button
                      type="button"
                      className={styles.previewButton}
                      disabled={disabled}
                      onClick={() => handlePreview(file)}
                      aria-label="View briefing material"
                      title={
                        disabled
                          ? "Request access to preview"
                          : "View briefing material"
                      }
                    >
                      <img src={viewIcon} alt="" aria-hidden="true" />
                      <span>View</span>
                    </button>

                    <button
                      type="button"
                      className={styles.downloadButton}
                      disabled={disabled}
                      onClick={() => handleDownload(file)}
                      aria-label="Download briefing material"
                      title={
                        disabled
                          ? "Request access to download"
                          : "Download briefing material"
                      }
                    >
                      <img src={downloadIcon} alt="" aria-hidden="true" />
                      <span>Download</span>
                    </button>
                  </div>
                </article>
              );
            })}
          </div>
        ) : null}
      </div>

      {downloading ? (
        <div className={styles.downloadOverlay}>
          <div className={styles.progressBox}>
            <p>
              {type} {downloadProgress}%
            </p>
            <div className={styles.progressBar}>
              <div
                className={styles.progressFill}
                style={{ width: `${downloadProgress}%` }}
              />
            </div>
          </div>
        </div>
      ) : null}

      <DocumentPreviewModal
        isOpen={isModalOpen}
        file={previewFile}
        onClose={() => {
          if (previewFile?.blobUrl) {
            window.URL.revokeObjectURL(previewFile.blobUrl);
          }
          setIsModalOpen(false);
          setPreviewFile(null);
        }}
      />
    </>
  );
};

export default VideoBriefing;

